// Sends browser-side errors to the Worker (POST /api/client-errors), where they land in the
// same logs as server errors as `client.error`. Without this, a crash on someone's phone is
// invisible to us.
//
// It never sends the page's query string or fragment (a sign-in link carries its token in the
// fragment), and caps reports per page load so a render loop can't flood the logs.
import { ApiError } from "./api";

export type ReportSource = "window" | "promise" | "react" | "sync" | "api" | "extract" | "push" | "other";
export type Severity = "error" | "notice";

export const MAX_REPORTS_PER_PAGE = 10;
let sent = 0;
const seen = new Set<string>();

declare const __RELEASE__: string;
const release = typeof __RELEASE__ === "string" ? __RELEASE__ : "dev";

export function resetReports() {
  sent = 0;
  seen.clear();
}

function describe(err: unknown): { name?: string; message: string; stack?: string } {
  if (err instanceof Error) return { name: err.name, message: err.message || err.name, stack: err.stack };
  if (typeof err === "string") return { message: err };
  try {
    return { message: JSON.stringify(err) ?? String(err) };
  } catch {
    return { message: String(err) };
  }
}

/**
 * Reports an error. API errors are skipped: the server already logged them, with more detail.
 * `notice` is for expected trouble worth seeing on staging (a damaged PDF), not a bug.
 */
export function reportError(source: ReportSource, err: unknown, context: Record<string, string | number | boolean | null> = {}, severity: Severity = "error"): void {
  if (err instanceof ApiError) return;
  const info = describe(err);
  // Browser extensions and cross-origin scripts produce this with no detail; it isn't ours.
  if (source === "window" && info.message === "Script error.") return;
  const key = `${source}:${info.message}`;
  if (seen.has(key) || sent >= MAX_REPORTS_PER_PAGE) return;
  seen.add(key);
  sent++;
  if (import.meta.env.DEV) console.error(`[report:${source}]`, err, context);
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;

  const body = {
    source,
    severity,
    ...info,
    path: location.pathname,
    release,
    online: navigator.onLine,
    context,
  };
  try {
    void fetch("/api/client-errors", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      credentials: "same-origin",
      // Lets the report finish even if the page is closing.
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Reporting must never break the app.
  }
}

/** Catches uncaught errors and unhandled promise rejections anywhere in the page. */
export function installErrorReporting(target: Window = window): void {
  target.addEventListener("error", (e) => reportError("window", e.error ?? e.message, { file: e.filename?.split("?")[0] ?? null, line: e.lineno ?? null }));
  target.addEventListener("unhandledrejection", (e) => reportError("promise", e.reason));
}
